import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Typography, useMediaQuery } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { CardSkeleton } from '@/components/cards/card-skeleton/card-skeleton';
import ExperienceCard from '@/components/cards/experience-card/experience-card';
import {
  ExperienceFilter,
  ExperienceMode,
  ExperienceSortFields,
  SortDirection,
  useExperiencesQuery,
} from '@/generated/graphql';
import { useExperienceFilter } from '../hooks/use-experience-filter';
import useAutoLoadMore from '../hooks/use-auto-load-more';
import { ExperiencesNotFound } from './experiences-not-found';
import { useSearchParams } from 'next/navigation';
import useSetTag from '../hooks/use-set-tag';

const ResultsDiv = styled.div`
  width: 100%;
  height: auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 1fr;
  gap: 32px;
  row-gap: 64px;
  position: relative;
  z-index: 2;
  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1920px) {
    grid-template-columns: repeat(5, 1fr);
  }
  @media (max-width: 768px) {
    padding: 0;
    row-gap: 0;
    grid-template-columns: repeat(1, 1fr);
    width: max-content;
  }
`;
const Title = styled(Typography)`
  margin-bottom: 32px;
  font-family: var(--font-work-sans);
  font-weight: 500;
  font-size: 22px;
  line-height: 127.3%;
  color: #251628;
  position: relative;
  z-index: 9;
`;
const ResultCard = styled.div`
  height: 323px;
  max-width: 290px;
  flex: 30%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    min-width: 100%;
    max-width: 350px;
    margin-bottom: 32px;
  }
`;

const LoadMoreButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
`;

const beatBars = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

const BarsIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 32px;
`;

const Bar = styled.div<{ delay: string }>`
  width: 4px;
  height: 100%;
  background-color: #f8d057;
  border-radius: 2px;
  transform-origin: center;
  animation: ${beatBars} 0.75s ease-in-out infinite;
  animation-delay: ${props => props.delay};
`;

const StyledExperiencesNotFound = styled(ExperiencesNotFound)`
  color: white;
`;

export const ExperienceGrid = () => {
  const bigScreen = useMediaQuery('(min-width:1920px)');
  const medScreen = useMediaQuery('(min-width:1440px)');
  const setTag = useSetTag();

  const searchParams = useSearchParams();
  const tag: string | null = searchParams?.get('tag');

  // set tag in filter context when it first appears or changes
  useEffect(() => {
    if (tag) {
      setTag(tag);
    }
  }, [tag, setTag]);

  const numItems = bigScreen ? 5 : medScreen ? 4 : 3;

  const PAGE_SIZE = numItems * 2;
  const { mode, filter } = useExperienceFilter();
  const experienceFilter = {
    ...filter,
  } as ExperienceFilter;

  const { data, loading, fetchMore } = useExperiencesQuery({
    variables: {
      filter: experienceFilter,
      sorting: {
        field: ExperienceSortFields.Next,
        direction: SortDirection.Asc,
      },
      paging: {
        first: PAGE_SIZE,
      },
    },
  });

  const hasNextPage = data?.experiences?.pageInfo?.hasNextPage;
  const endCursor = data?.experiences?.pageInfo?.endCursor;
  const experiences = tag
    ? data?.experiences?.edges?.filter(
        edge => edge?.node?.experienceTags?.edges, //?.some(t => t?.node?.tag?.title === tag),
      )
    : data?.experiences?.edges;

  const totalCount = data?.experiences?.totalCount ?? experiences?.length ?? 0;
  const displayedCount = experiences?.length ?? 0;
  const titleCount = hasNextPage ? totalCount : displayedCount;

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage) {
      return Promise.resolve();
    }

    return fetchMore({
      variables: {
        filter: experienceFilter,
        sorting: {
          field: ExperienceSortFields.Next,
          direction: SortDirection.Asc,
        },
        paging: {
          first: PAGE_SIZE,
          after: endCursor,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.experiences?.edges?.length) {
          return previousResult;
        }

        const prevEdges = previousResult.experiences?.edges || [];
        const nextEdges = fetchMoreResult.experiences.edges || [];
        const existingIds = new Set(prevEdges.map(edge => edge?.node?.id));
        const mergedEdges = [
          ...prevEdges,
          ...nextEdges.filter(edge => !existingIds.has(edge?.node?.id)),
        ];

        return {
          ...fetchMoreResult,
          experiences: {
            ...fetchMoreResult.experiences,
            edges: mergedEdges,
          },
        };
      },
    });
  }, [hasNextPage, fetchMore, experienceFilter, PAGE_SIZE, endCursor]);

  const { isLoadingMore, loadMoreRef, triggerLoadMore } = useAutoLoadMore({
    enabled: Boolean(hasNextPage) && !loading,
    cursor: endCursor,
    onLoadMore: handleLoadMore,
  });

  if (!loading && !data) {
    return null;
  }

  return (
    <>
      <Title className={mode === ExperienceMode.Online ? 'Online' : ''}>
        {loading ? 'Loading' : titleCount} experiences
      </Title>
      <ResultsDiv>
        {loading ? (
          [0, 1, 2, 3, 4, 5].map(key => (
            <ResultCard key={key}>
              <CardSkeleton />
            </ResultCard>
          ))
        ) : (
          <>
            {experiences?.map(experience => (
              <ResultCard key={experience.node.id}>
                <ExperienceCard experience={experience.node} noCarousel />
              </ResultCard>
            ))}
          </>
        )}
        {!loading && (!experiences || !experiences.length) && <StyledExperiencesNotFound />}
      </ResultsDiv>
      {(hasNextPage || isLoadingMore) && (
        <LoadMoreButtonContainer ref={loadMoreRef}>
          <BarsIndicator>
            {['0s', '0.12s', '0.24s', '0.36s', '0.48s'].map(delay => (
              <Bar key={delay} delay={delay} />
            ))}
          </BarsIndicator>
        </LoadMoreButtonContainer>
      )}
    </>
  );
};
